import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import ProductCard from "../components/productCard";
import { CATEGORIES } from "../constants";
import type { Category, Product, ProductVariant } from "../types";
import { groupVariants } from "../utils/groupVariants";

const slugToCategory = (slug: string): Category | null => {
  const label = slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ") as Category;
  return (CATEGORIES as string[]).includes(label) ? label : null;
};

const CategoryPage = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const category = slugToCategory(slug);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!category) return;
    const fetchProducts = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const params = new URLSearchParams();
        if (category !== "New Arrivals") params.set("category", category);
        const res = await fetch(`${apiUrl}/api/v1/products?${params}`);
        if (res.ok) {
          const variants: ProductVariant[] = await res.json();
          const grouped = groupVariants(variants);
          setProducts(
            category === "New Arrivals"
              ? grouped.filter((p) => p.isNew)
              : grouped
          );
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [category]);

  if (!category) {
    return (
      <Box sx={{ textAlign: "center", py: 16 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Category not found
        </Typography>
        <Typography
          component={RouterLink}
          to="/"
          sx={{ color: "black", fontSize: "0.8rem", letterSpacing: "0.1em" }}
        >
          Back to shop
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="main">
      <Box
        sx={{
          borderBottom: "1px solid #e5e5e5",
          px: { xs: 2, md: 8 },
          py: 5,
          maxWidth: "80rem",
          mx: "auto",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {category}
        </Typography>
        {!loading && (
          <Typography
            variant="caption"
            sx={{ color: "#737373", mt: 1, display: "block" }}
          >
            {products.length} {products.length === 1 ? "item" : "items"}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          maxWidth: "80rem",
          mx: "auto",
          px: { xs: 2, md: 8 },
          py: { xs: 6, md: 8 },
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
            <CircularProgress sx={{ color: "black" }} />
          </Box>
        ) : products.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 10 }}>
            <Typography variant="body2" sx={{ color: "#737373" }}>
              No products are currently available in this category.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 3, md: 4 },
            }}
          >
            {products.map((product) => (
              <ProductCard key={product.name} product={product} />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CategoryPage;
